(() => {
  if (window.ZarfCartSize) return;

  const root = () => window.Shopify?.routes?.root || '/';
  const request = async (path, body) => {
    const response = await fetch(`${root()}${path}`, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : { cache: 'no-store' }),
    });
    const data = await response.json();
    if (!response.ok || data.errors || data.status >= 400) {
      throw new Error(typeof data.description === 'string' ? data.description : 'This size could not be added. Please try again.');
    }
    return data;
  };
  const propertiesKey = properties => JSON.stringify((Array.isArray(properties) ? properties : Object.entries(properties || {}))
    .filter(([, value]) => value != null && value !== '')
    .sort(([a], [b]) => a.localeCompare(b)));
  const planId = item => item.selling_plan_allocation?.selling_plan?.id || null;
  const sameLine = (item, reference, variantId = reference.variant_id) =>
    String(item.variant_id) === String(variantId)
    && propertiesKey(item.properties) === propertiesKey(reference.properties)
    && String(planId(item) || '') === String(planId(reference) || '');
  const matching = (cart, source, variantId) => cart.items.filter(item => sameLine(item, source, variantId));
  const total = items => items.reduce((sum, item) => sum + item.quantity, 0);
  const reviewError = () => new Error('The size change could not finish. Please review your cart before checking out.');
  const sectionIds = ['cart-drawer', 'cart-icon-bubble'];
  const sectionRequest = () => ({ sections: sectionIds, sections_url: window.location?.pathname || '/' });

  // Add first so an unavailable replacement never removes the customer's original item.
  async function replaceLine({ key, variantId, quantity, sourceVariantId, sourceProperties, sourceSellingPlanId }, api = request) {
    const before = await api('cart.js');
    let source = before.items.find(item => item.key === key);
    if (!source) {
      // Liquid's rendered key can be stale after an automatic discount or cart refresh.
      // Match the full line identity, never a row index or the first product found.
      const originalVariantId = sourceVariantId || (/^\d+:/.test(key || '') ? key.split(':')[0] : null);
      const candidates = before.items.filter(item =>
        originalVariantId && String(item.variant_id) === String(originalVariantId)
        && (sourceProperties === undefined || propertiesKey(item.properties) === propertiesKey(sourceProperties))
        && (sourceSellingPlanId === undefined || String(planId(item) || '') === String(sourceSellingPlanId || ''))
      );
      if (candidates.length === 1) source = candidates[0];
    }
    if (!source) throw new Error('Your cart changed. Please choose the size again.');
    const targetId = Number(variantId);
    if (!Number.isSafeInteger(targetId) || targetId <= 0) throw new Error('This size is unavailable.');
    if (targetId === source.variant_id) return before;
    const amount = quantity == null ? source.quantity : Number(quantity);
    if (!Number.isInteger(amount) || amount < 1 || amount > source.quantity) {
      throw new Error('Your cart quantity changed. Please choose the size again.');
    }
    const sourceTotal = total(matching(before, source));
    const targetTotal = total(matching(before, source, targetId));
    const addItem = { id: targetId, quantity: amount, properties: source.properties || {} };
    if (planId(source)) addItem.selling_plan = planId(source);

    try {
      await api('cart/add.js', { items: [addItem] });
      const added = await api('cart.js');
      if (total(matching(added, source, targetId)) !== targetTotal + amount) throw reviewError();
      // Automatic discounts may change a line key after adding the replacement.
      const sourceMatches = matching(added, source);
      const current = sourceMatches.find(item => item.key === source.key)
        || (sourceMatches.length === 1 ? sourceMatches[0] : null);
      if (!current || current.quantity !== source.quantity) throw reviewError();
      return await api('cart/change.js', {
        id: current.key,
        quantity: current.quantity - amount,
        ...sectionRequest(),
      });
    } catch (error) {
      // A failed response can still represent a committed request. Reconcile before retrying anything.
      let current;
      try { current = await api('cart.js'); } catch { throw reviewError(); }
      const remainingSource = total(matching(current, source));
      let addedAmount = total(matching(current, source, targetId)) - targetTotal;
      if (remainingSource === sourceTotal - amount && addedAmount === amount) return current;
      if (remainingSource !== sourceTotal || addedAmount < 0 || addedAmount > amount) throw reviewError();
      // Restore only what this operation added, including partial inventory additions.
      try {
        while (addedAmount > 0) {
          const target = matching(current, source, targetId).at(-1);
          if (!target) throw reviewError();
          const remove = Math.min(addedAmount, target.quantity);
          current = await api('cart/change.js', { id: target.key, quantity: target.quantity - remove });
          const remainingAdded = total(matching(current, source, targetId)) - targetTotal;
          if (remainingAdded < 0 || remainingAdded >= addedAmount) throw reviewError();
          addedAmount = remainingAdded;
        }
      } catch { throw reviewError(); }
      throw error;
    }
  }

  let busy = false;
  let revision = 0;
  window.ZarfCartSize = { replaceLine, get busy() { return busy; }, get revision() { return revision; } };

  ['change', 'submit'].forEach(type => document.addEventListener(type, event => {
    if (busy && event.target.closest('#CartDrawer')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true));

  function showError(message) {
    const element = document.getElementById('CartDrawer-CartErrors');
    if (element) {
      element.classList.add('zarf-size-status');
      element.textContent = message;
    }
  }

  function renderDrawer(cart) {
    if (!cart.sections?.['cart-drawer'] || !cart.sections?.['cart-icon-bubble']) return false;
    const parser = new DOMParser();
    const html = parser.parseFromString(cart.sections['cart-drawer'], 'text/html');
    const nextInner = html.querySelector('#CartDrawer .drawer__inner');
    const currentInner = document.querySelector('#CartDrawer .drawer__inner');
    if (!nextInner || !currentInner) return false;

    // Reject stale section HTML rather than show a previous size as the completed result.
    const signature = items => items.map(item => `${item.variant_id}:${item.quantity}`).sort().join('|');
    const rows = Array.from(nextInner.querySelectorAll('tr.cart-item')).map(row => {
      const input = row.querySelector('[data-quantity-variant-id]');
      return { variant_id: input?.dataset.quantityVariantId, quantity: Number(input?.value) };
    });
    if (signature(rows) !== signature(cart.items)) return false;

    const bubble = document.getElementById('cart-icon-bubble');
    const bubbleHTML = parser.parseFromString(cart.sections['cart-icon-bubble'], 'text/html')
      .querySelector('.shopify-section');
    // Keep the drawer shell (and its open state, overlay listener and focus trap) intact.
    currentInner.innerHTML = nextInner.innerHTML;
    currentInner.classList.toggle('is-empty', cart.item_count === 0);
    document.querySelector('cart-drawer')?.classList.toggle('is-empty', cart.item_count === 0);
    if (bubble) bubble.innerHTML = bubbleHTML ? bubbleHTML.innerHTML : cart.sections['cart-icon-bubble'];
    window.zarfTryApplyBuy2At9999VisualPricing?.();
    return true;
  }

  async function refreshDrawer(cart) {
    if (cart && renderDrawer(cart)) return cart;
    // A null/missing section or a lost mutation response gets a fresh bundled render.
    // Empty updates preserve quantities; the HTML and cart data come from one response.
    const current = await request('cart/update.js', { updates: {}, ...sectionRequest() });
    if (!renderDrawer(current)) throw new Error('Your cart was updated, but the drawer could not refresh. Please reload the page.');
    return current;
  }

  document.addEventListener('click', async event => {
    const drawer = event.target.closest('#CartDrawer');
    if (!drawer) return;
    if (busy && !event.target.closest('.drawer__close, #CartDrawer-Overlay')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const button = event.target.closest('[data-cart-size-option]');
    if (!button || button.disabled) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (document.body.classList.contains('zarf-cart-variant-updating')) return;

    const row = button.closest('[data-cart-line-key]');
    if (!row) return;
    busy = true;
    revision += 1;
    drawer.setAttribute('aria-busy', 'true');
    showError('');
    const disabledControls = Array.from(drawer.querySelectorAll('button:not(.drawer__close), input, select, textarea'))
      .map(control => [control, control.disabled]);
    disabledControls.forEach(([control]) => { control.disabled = true; });
    window.zarfSetCartVariantUpdateLoading?.(true);
    const loader = drawer.querySelector('.zarf-cart-discount-loader__text');
    if (loader) loader.textContent = 'Updating size…';
    let failure = '';
    let cart;
    try {
      const sourceRow = row.dataset.zarfSourceRowId
        ? document.getElementById(row.dataset.zarfSourceRowId) || row
        : row;
      const sourceInput = sourceRow.querySelector('[data-quantity-variant-id]');
      cart = await replaceLine({
        key: row.dataset.cartLineKey,
        sourceVariantId: sourceRow.dataset.cartVariantId || sourceInput?.dataset.quantityVariantId,
        sourceProperties: sourceRow.dataset.cartProperties === undefined
          ? undefined : JSON.parse(sourceRow.dataset.cartProperties),
        sourceSellingPlanId: sourceRow.dataset.cartSellingPlanId,
        variantId: button.dataset.variantId,
        // Offer rows can display only part of a real Shopify line.
        quantity: row.dataset.zarfVisualGroupQuantity || null,
      });
    } catch (error) {
      failure = error.message;
    }
    try {
      cart = await refreshDrawer(cart);
      if (!failure) {
        // The offer adjustment also renders through this same drawer path.
        let normalizedRenderFailed = false;
        await window.zarfNormalizeAcBlanketCart?.({
          getSectionsToRender: () => sectionIds.map(section => ({ section })),
          renderCartSections: state => {
            cart = state;
            normalizedRenderFailed = !renderDrawer(state);
          },
        });
        if (normalizedRenderFailed) cart = await refreshDrawer(cart);
      }
      if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
    } catch (error) {
      failure = failure || error.message;
    } finally {
      busy = false;
      drawer.removeAttribute('aria-busy');
      disabledControls.forEach(([control, wasDisabled]) => { control.disabled = wasDisabled; });
      window.zarfSetCartVariantUpdateLoading?.(false);
      const loaderText = drawer.querySelector('.zarf-cart-discount-loader__text');
      if (loaderText) loaderText.textContent = 'Unlocking deal...';
      if (failure) showError(failure);
      const focusTarget = drawer.querySelector(`[data-cart-size-option][data-variant-id="${button.dataset.variantId}"][aria-pressed="true"]`)
        ?.closest('.cart-item')?.querySelector('.cart-item__name') || drawer.querySelector('.drawer__close');
      if (focusTarget) focusTarget.focus();
    }
  }, true);
})();
